import Box from '@mui/material/Box'
import Popover from '@mui/material/Popover'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useState } from 'react'
import { UInput } from './UInput'

// Get base URL from Vite config (e.g., '/')
const baseUrl = import.meta.env.BASE_URL

export interface USelectOption {
    value: string
    label: string
}

interface USelectProps {
    label: string
    name?: string
    value: string
    onChange: (value: string) => void
    options: USelectOption[]
    placeholder?: string
    disabled?: boolean
    error?: boolean
    helperText?: string
}

/**
 * USelect - Select component with Popover for GLIC2027
 * Based on U25 SelectPopoverComponent pattern
 */
const USelect = ({ label, name, value, onChange, options, placeholder, disabled = false, error = false, helperText }: USelectProps) => {
    const [open, setOpen] = useState(false)
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

    // Find the selected option label
    const selectedOption = options.find(opt => opt.value === value)
    const displayValue = selectedOption ? selectedOption.label : value

    const handleOpen = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!disabled) {
            setAnchorEl(e.currentTarget)
            setOpen(true)
        }
    }

    const handleClose = () => {
        setOpen(false)
        setAnchorEl(null)
    }

    const handleSelect = (optionValue: string) => {
        onChange(optionValue)
        handleClose()
    }

    return (
        <Stack sx={{ position: 'relative', width: '100%' }}>
            <Box
                sx={{
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    zIndex: 1
                }}
                onClick={handleOpen}
            />
            <UInput
                label={label}
                name={name}
                value={displayValue}
                onChange={() => {}}
                placeholder={placeholder}
                disabled={disabled}
                error={error}
                helperText={helperText}
                slotProps={{
                    input: {
                        readOnly: true,
                        sx: {
                            cursor: disabled ? 'not-allowed' : 'pointer',
                            paddingRight: '40px'
                        }
                    }
                }}
                sx={{
                    '& .MuiInputLabel-root': {
                        color: !value ? 'rgba(0, 59, 111, 1)!important' : 'rgba(98, 116, 142, 1)!important'
                    }
                }}
            />
            {/* Dropdown Icon */}
            <Box
                component="img"
                src={`${baseUrl}assets/icons/DropdownIcon.svg`}
                alt="dropdown"
                sx={{
                    position: 'absolute',
                    right: '14px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '24px',
                    height: '24px',
                    pointerEvents: 'none',
                    opacity: disabled ? 0.5 : 1,
                    zIndex: 0
                }}
            />
            <Popover
                open={open}
                onClose={handleClose}
                anchorEl={anchorEl}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                }}
                sx={{
                    '& .MuiPopover-paper': {
                        borderRadius: '8px',
                        boxShadow: '0px 5px 5px -3px rgba(0, 0, 0, 0.2), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0, 0, 0, 0.12)',
                        maxHeight: '300px',
                        overflow: 'auto'
                    },
                    marginTop: '8px'
                }}
                slotProps={{
                    paper: {
                        style: {
                            width: anchorEl?.offsetWidth || 'auto'
                        }
                    }
                }}>
                <Stack padding="8px 0px" bgcolor="background.default">
                    {options.map(option => (
                        <Stack
                            key={option.value}
                            padding="0px 16px"
                            sx={{
                                cursor: 'pointer',
                                bgcolor: value === option.value ? 'warning.dark' : 'background.default',

                                '&:hover': {
                                    bgcolor: 'transparent'
                                },
                                height: '48px',
                                justifyContent: 'center'
                            }}
                            onClick={() => handleSelect(option.value)}>
                            <Typography variant="body1" fontSize="16px" color="primary">
                                {option.label}
                            </Typography>
                        </Stack>
                    ))}
                </Stack>
            </Popover>
        </Stack>
    )
}

export default USelect
